import { Build, Host, h } from "@stencil/core";
import { getSvgContent, pdsIconContent } from "./request";
import { getName, getUrl, inheritAttributes, isRTL, shouldRtlFlipIcon } from "./utils";
export class PdsIcon {
    constructor() {
        this.didLoadIcon = false;
        this.iconName = null;
        this.inheritedAttributes = {}; // eslint-disable-line @typescript-eslint/no-explicit-any
        this.isVisible = false;
        /**
         * The size of the icon. This can be
         * 'small', 'regular', 'medium', 'large', or a
         * custom value (40px, 1rem, etc)
         *
         */
        this.size = 'regular';
        this.hasAriaHidden = () => {
            const { el } = this;
            return el.hasAttribute('aria-hidden') && el.getAttribute('aria-hidden') === 'true';
        };
    }
    iconSize() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sizes = {
            small: '12px',
            regular: '16px',
            medium: '20px',
            large: '24px',
        };
        if (sizes[this.size]) {
            return sizes[this.size];
        }
        else {
            return this.size;
        }
    }
    componentDidLoad() {
        this.setCSSVariables();
        if (!this.didLoadIcon) {
            this.loadIcon();
        }
    }
    componentWillLoad() {
        this.inheritedAttributes = inheritAttributes(this.el, ['aria-label']);
        this.setCSSVariables();
    }
    setCSSVariables() {
        this.el.style.setProperty(`--dimension-icon-height`, this.iconSize());
        this.el.style.setProperty(`--dimension-icon-width`, this.iconSize());
        this.el.style.setProperty(`--color-icon-fill`, typeof this.color !== 'undefined' ? this.color : 'currentColor');
    }
    connectedCallback() {
        this.waitUntilVisible(this.el, '50px', () => {
            this.isVisible = true;
            this.loadIcon();
        });
    }
    disconnectedCallback() {
        if (this.io) {
            this.io.disconnect();
            this.io = undefined;
        }
    }
    updateStyles() {
        this.setCSSVariables();
    }
    loadIcon() {
        if (Build.isBrowser && this.isVisible) {
            const url = getUrl(this);
            if (url) {
                if (pdsIconContent.has(url)) {
                    this.svgContent = pdsIconContent.get(url);
                }
                else {
                    getSvgContent(url).then(() => (this.svgContent = pdsIconContent.get(url)));
                }
                this.didLoadIcon = true;
            }
        }
        this.iconName = getName(this.name, this.icon);
        if (this.iconName) {
            this.ariaLabel = this.iconName.replace(/\-/g, ' ');
        }
    }
    render() {
        const { ariaLabel, flipRtl, iconName, inheritedAttributes } = this;
        const shouldIconAutoFlip = iconName
            ? shouldRtlFlipIcon(iconName, this.el) && flipRtl !== false
            : false;
        const shouldFlip = flipRtl || shouldIconAutoFlip;
        return (h(Host, Object.assign({ key: 'bc8e46daa75df27a8039e0e767bdb43164ad7d4c', "aria-label": ariaLabel !== undefined && !this.hasAriaHidden() ? ariaLabel : null, alt: "", role: "img", class: Object.assign(Object.assign({}, createColorClasses(this.color)), { 'flip-rtl': shouldFlip, 'icon-rtl': shouldFlip && isRTL(this.el) }) }, inheritedAttributes), Build.isBrowser && this.svgContent ? (h("div", { class: "icon-inner", innerHTML: this.svgContent })) : (h("div", { class: "icon-inner" }))));
    }
    /*****
     * Private Methods
     ****/
    waitUntilVisible(el, rootMargin, cb) {
        if (Build.isBrowser && typeof window !== 'undefined' && (window).IntersectionObserver) {
            const io = (this.io = new (window).IntersectionObserver((data) => {
                if (data[0].isIntersecting) {
                    io.disconnect();
                    this.io = undefined;
                    cb();
                }
            }, { rootMargin }));
            io.observe(el);
        }
        else {
            // browser doesn't support IntersectionObserver
            // so just fallback to always show it
            cb();
        }
    }
    static get is() { return "pds-icon"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["pds-icon.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["pds-icon.css"]
        };
    }
    static get assetsDirs() { return ["svg"]; }
    static get properties() {
        return {
            "color": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "\nThe color of the icon"
                },
                "getter": false,
                "setter": false,
                "attribute": "color",
                "reflect": false
            },
            "flipRtl": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Determines if the icon should be flipped when the `dir` is right-to-left (`\"rtl\"`).\nThis is automatically enabled for icons that are in the `ICONS_TO_FLIP` list and\nwhen the `dir` is `\"rtl\"`. If `flipRtl` is set to `false`, the icon will not be flipped\neven if the `dir` is `\"rtl\"`."
                },
                "getter": false,
                "setter": false,
                "attribute": "flip-rtl",
                "reflect": false
            },
            "icon": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This is a combination of both `name` and `src`. If a `src` URL is detected,\nit will set the `src` property. Otherwise it assumes it's a built-in named\nSVG and sets the `name` property."
                },
                "getter": false,
                "setter": false,
                "attribute": "icon",
                "reflect": false
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The name of the icon to use from\nthe built-in set."
                },
                "getter": false,
                "setter": false,
                "attribute": "name",
                "reflect": true
            },
            "size": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "| 'small'   // 12px\n    | 'regular'  // 16px\n    | 'medium'  // 20px\n    | 'large'   // 24px\n    | 'auto'\n    | string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The size of the icon. This can be\n'small', 'regular', 'medium', 'large', or a\ncustom value (40px, 1rem, etc)"
                },
                "getter": false,
                "setter": false,
                "attribute": "size",
                "reflect": true,
                "defaultValue": "'regular'"
            },
            "src": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "\nSpecifies the exact `src` of an SVG file to use."
                },
                "getter": false,
                "setter": false,
                "attribute": "src",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "ariaLabel": {},
            "isVisible": {},
            "svgContent": {}
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "size",
                "methodName": "updateStyles"
            }, {
                "propName": "color",
                "methodName": "updateStyles"
            }, {
                "propName": "name",
                "methodName": "loadIcon"
            }, {
                "propName": "src",
                "methodName": "loadIcon"
            }, {
                "propName": "icon",
                "methodName": "loadIcon"
            }];
    }
}
const createColorClasses = (color) => {
    return color
        ? {
            'pds-color': true,
            [`pds-color-${color}`]: true,
        }
        : null;
};
//# sourceMappingURL=pds-icon.js.map
